import React, { useCallback, useState } from 'react';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Box, Checkbox, ClickAwayListener, Paper, Popper, Typography } from '@mui/material';

import type { ColumnDef } from './DataTable';

interface ColumnConfigProps {
  allColumns: ColumnDef[];
  visibleKeys: string[];
  onChange: (visibleKeys: string[], orderedColumns: ColumnDef[]) => void;
}

function SortableColumnItem({
  column,
  isVisible,
  onToggle,
}: {
  column: ColumnDef;
  isVisible: boolean;
  onToggle: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: column.key,
  });

  return (
    <Box
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        py: 0.5,
        px: 1,
        borderRadius: '4px',
        '&:hover': { bgcolor: 'grey.50' },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{ cursor: 'grab', display: 'flex', color: 'grey.400', '&:active': { cursor: 'grabbing' } }}
      >
        <DragIndicatorIcon sx={{ fontSize: 16 }} />
      </Box>
      <Checkbox
        size="small"
        checked={isVisible}
        disabled={column.required}
        onChange={onToggle}
        sx={{ p: 0 }}
      />
      <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.primary' }}>
        {column.label}
      </Typography>
    </Box>
  );
}

export const ColumnConfig: React.FC<ColumnConfigProps> = ({ allColumns, visibleKeys, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleToggle = useCallback(
    (key: string) => {
      const col = allColumns.find(c => c.key === key);
      if (col?.required) return;
      const next = visibleKeys.includes(key)
        ? visibleKeys.filter(k => k !== key)
        : [...visibleKeys, key];
      onChange(next, allColumns);
    },
    [visibleKeys, allColumns, onChange],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const oldIdx = allColumns.findIndex(c => c.key === active.id);
      const newIdx = allColumns.findIndex(c => c.key === over.id);
      const reordered = arrayMove(allColumns, oldIdx, newIdx);
      onChange(visibleKeys, reordered);
    },
    [allColumns, visibleKeys, onChange],
  );

  return (
    <>
      <Box
        onClick={e => setAnchorEl(open ? null : e.currentTarget)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1.25,
          py: 0.5,
          borderRadius: '6px',
          border: '1px solid',
          borderColor: open ? 'divider' : 'transparent',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'grey.50' },
        }}
      >
        <ViewColumnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          Columns
        </Typography>
      </Box>

      <Popper open={open} anchorEl={anchorEl} placement="bottom-end" sx={{ zIndex: 1300 }}>
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Paper elevation={3} sx={{ p: 1.5, minWidth: 200, mt: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.5625rem',
                textTransform: 'uppercase',
                color: 'text.secondary',
                fontWeight: 600,
                letterSpacing: '0.06em',
                px: 1,
                mb: 0.5,
                display: 'block',
              }}
            >
              Drag to reorder
            </Typography>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={allColumns.map(c => c.key)}
                strategy={verticalListSortingStrategy}
              >
                {allColumns.map(col => (
                  <SortableColumnItem
                    key={col.key}
                    column={col}
                    isVisible={visibleKeys.includes(col.key)}
                    onToggle={() => handleToggle(col.key)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
